/**
 * CE QU'UN ECHEC DE CONNEXION DIT A QUELQU'UN QUI NE CODE PAS.
 *
 * Firebase rend des messages comme « Firebase: Error (auth/invalid-credential). »
 * Personne ne sait quoi en faire, et sur un ecran de connexion c'est le pire
 * endroit possible pour ne pas comprendre : on ne peut rien faire d'autre que
 * reessayer la meme chose.
 *
 * TROIS CAS VALENT LA TRADUCTION, les autres tombent dans le repli :
 *   - les identifiants faux, que Firebase regroupe depuis 2023 sous un code
 *     unique (`invalid-credential`) pour ne pas dire si c'est l'adresse ou le
 *     mot de passe qui cloche. On ne le dit pas non plus : c'est volontaire, et
 *     ca ne change rien pour qui connait les siens ;
 *   - la fenetre Google fermee ou bloquee. Fermer la fenetre est un GESTE, pas
 *     une panne : ca ne doit pas laisser une ligne rouge derriere soi ;
 *   - l'adresse deja prise par l'autre methode. C'est le seul message qui dit
 *     quoi faire, parce que la personne a un compte et ne le sait pas.
 *
 * Fonction PURE, testee sans navigateur. `null` veut dire « ne montre rien ».
 */

/** Le code Firebase porte par une erreur, sans son prefixe. */
function codeDe(cause: unknown): string {
  if (typeof cause !== 'object' || cause === null) return '';
  const brut = Reflect.get(cause, 'code');
  return typeof brut === 'string' ? brut.replace(/^auth\//, '') : '';
}

/**
 * Le message a afficher, ou `null` quand il n'y a rien a dire.
 *
 * @param cause - l'erreur levee par `@taki/auth`.
 */
export function messageDeConnexion(cause: unknown): string | null {
  switch (codeDe(cause)) {
    // Un geste, pas une panne : la personne a referme la fenetre Google.
    case 'popup-closed-by-user':
    case 'cancelled-popup-request':
    case 'user-cancelled':
      return null;

    case 'popup-blocked':
      return "La fenetre Google a ete bloquee par le navigateur. Autorise les fenetres pour ce site.";

    case 'invalid-credential':
    case 'wrong-password':
    case 'user-not-found':
    case 'invalid-email':
      return 'Adresse ou mot de passe incorrect.';

    case 'user-disabled':
      return 'Ce compte est desactive.';

    case 'too-many-requests':
      return 'Trop de tentatives. Reessaie dans quelques minutes.';

    case 'account-exists-with-different-credential':
      return "Cette adresse a deja un mot de passe : entre avec, plutot qu'avec Google.";

    case 'operation-not-allowed':
      return "La connexion avec Google n'est pas activee sur cet environnement.";

    case 'network-request-failed':
      return 'Connexion au serveur impossible. Verifie ta liaison reseau.';

    default: {
      if (cause instanceof Error && cause.message !== '') return cause.message;
      return 'Echec de la connexion.';
    }
  }
}
